import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import useDownload from "../../../hooks/useDownload";
import {toast} from "react-toastify";
import {PageErrorMessage} from "../../../components/PageErrorMessage";
import Loader from "../../../components/common/Loader";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs/CustomBreadcrumbs";
import {FaLinkedin, FaLinkedinIn} from "react-icons/fa";
import AchievementsListCard from "../../../components/Achievements/AchievementsListCard";
import {Document, Page} from "react-pdf";
import {MasterClassService} from "../../../services/masterClassService/masterClassService";

const MasterClassCertificate = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [certificateDetails, setCertificateDetails] = useState(null);
    const { id } = useParams();
    const downloadFile = useDownload();
    const [error, setError] = useState(null);
    const [isCertificateLoading, setIsCertificateLoading] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [previewBlob, setPreviewBlob] = useState(null);

    const [file, setFile] = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const fetchFile = async () => {
            try {
                const response = await fetch(certificateDetails?.certificate);
                if (!response.ok) throw new Error(`Failed to fetch file`);
                const blob = await response.blob();
                setFile(blob);
            } catch (err) {
                console.error("Error loading PDF:", err);
            }
        };

        fetchFile();
    }, [certificateDetails?.certificate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const canvas = containerRef.current?.querySelector("canvas");
            if (!canvas) return;

            canvas.toBlob((blob) => {
                if (blob) {
                    setPreviewBlob(blob);
                }
            }, "image/png");
        }, 500);

        return () => clearTimeout(timer);
    }, [file]);

    const masterClassCertificateData = async () => {
        setIsLoading(true);
        try {
            await MasterClassService.getMasterClassCertificate(id).then((response) => {
                setCertificateDetails(response?.data);
            });
            setIsLoading(false);
        } catch (error) {
            setError({
                statusCode: error.response.status,
                message: error.response.data.message,
            });
            setIsLoading(false);
        }
    };

    // useEffect of call all api function
    useEffect(() => {
        masterClassCertificateData();
    }, [id]);

    const handleDownloadCertificate = async () => {
        try {
            setIsCertificateLoading(true);
            const certificate = await MasterClassService.downloadCertificate(certificateDetails?.master_class?.id);
            if (certificate.data.url) {
                const filename = (certificateDetails?.master_class?.name || "certificate").replace(/[^\w\s]/g, "_").replace(/\s+/g, "_");
                await downloadFile(certificate.data.url, filename);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to download certificate');
        } finally {
            setIsCertificateLoading(false)
        }
    };

    const shareOnLinkedin = async (type) => {
        if (!previewBlob) {
            toast.error("Certificate still loading");
            return;
        }

        setIsSharing(true);
        try {
            const formData = new FormData();
            formData.append('preview_image', previewBlob, 'preview.png');
            formData.append('type', type);

            const { data } = await MasterClassService.shareOnLinkedin(id, formData);
            window.open(data.share_url, '_blank');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to share');
        } finally {
            setIsSharing(false);
        }
    };

    if (error) {
        return <PageErrorMessage
            error={error}
            entityTitle="Masterclass Certificate"
        />;
    }

    return (
        <>
            {isLoading ? (
                <>
                    <Loader />
                </>
            ) : (
                <>
                    <div className="container m-4">
                        <div className="row">
                            <CustomBreadcrumbs
                                currentPageTitle="Certificate"
                                pages={[{
                                    title: certificateDetails?.master_class?.name,
                                    to: `/master-classes/${certificateDetails?.master_class?.id}`
                                }]}
                            />
                        </div>
                        <div className="row mb-3">
                            <h1>Masterclass Certificate</h1>
                        </div>
                        <div className="row mb-3 program__content">
                            <div className="col-md-6 text-center">
                                <div className="program__image" ref={containerRef}>
                                    <Document
                                        file={file}
                                    >
                                        <Page
                                            pageNumber={1}
                                        />
                                    </Document>
                                </div>
                            </div>

                            <div className="col-md-6 mb-4 mb-md-0">
                                <div className="d-flex align-center flex-wrap">
                                    <div className="position-relative d-inline-block mr-5">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleDownloadCertificate}
                                            disabled={isCertificateLoading}
                                        >
                                            {isCertificateLoading ? (
                                                <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                    Loading...
                                                </>
                                            ) : (
                                                "Download"
                                            )}
                                        </button>

                                        {isCertificateLoading && (
                                            <div
                                                className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 d-flex align-items-center justify-content-center rounded">
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {certificateDetails?.badges.length > 0 &&
                                    <div className="mt-2">
                                        <AchievementsListCard
                                            achievements={certificateDetails?.badges}
                                            title="Related Achievements"
                                        />
                                    </div>
                                }
                                <div className="user-profile-achievements p-3 mt-3">
                                    <h5>Share your certificate on social media:</h5>
                                    <div className="d-flex justify-content-start flex-wrap my-2">
                                        <div className="share-certificate-icon icon-bg cursor-pointer d-flex align-center" onClick={() => shareOnLinkedin('add')}>
                                            <FaLinkedinIn
                                                className={`fs-5 mr-5`}
                                            />
                                            <div className="fs-5 lh-base">
                                                Linkedin
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default MasterClassCertificate;